/**
 * Parsing constants — pipeline states, review queue vocabulary, Bedrock
 * model identifiers, and masters-type keys referenced by @loanaudit/parsing-core.
 *
 * Keep runtime values here (not types) so both backend and worker pick them
 * up identically. Adding a value requires:
 *   1. Update this file
 *   2. If state-machine-relevant, update PARSE_TRANSITIONS below
 *   3. If masters-relevant, update the seeder in backend/prisma/seed.ts
 */
// -------------------------------------------------------------------------
// Per-document parse state — tracked on ingestion.documents.parse_state.
// Independent of loan-file state (which is aggregated across all docs).
// -------------------------------------------------------------------------
export const DOCUMENT_PARSE_STATE = {
    PENDING: 'pending', // doc exists, parsing not yet started
    OCR_QUEUED: 'ocr_queued',
    OCR_RUNNING: 'ocr_running', // Textract async job launched, awaiting completion
    OCR_COMPLETE: 'ocr_complete',
    CLASSIFY_QUEUED: 'classify_queued',
    CLASSIFY_RUNNING: 'classify_running',
    CLASSIFY_COMPLETE: 'classify_complete',
    EXTRACT_QUEUED: 'extract_queued',
    EXTRACT_RUNNING: 'extract_running',
    EXTRACT_COMPLETE: 'extract_complete',
    NEEDS_REVIEW: 'needs_review', // low confidence routed to review queue
    PARSE_COMPLETE: 'parse_complete', // ready for validation (Sprint 6)
    FAILED: 'failed',
};
// -------------------------------------------------------------------------
// Parsing stage identifiers — used in parsing_jobs.stage column.
// -------------------------------------------------------------------------
export const PARSE_STAGE = {
    OCR: 'ocr',
    CLASSIFY: 'classify',
    EXTRACT: 'extract',
};
// -------------------------------------------------------------------------
// Parsing job status — mirrors ingestion_jobs.status.
// -------------------------------------------------------------------------
export const PARSING_JOB_STATUS = {
    QUEUED: 'queued',
    RUNNING: 'running',
    POLLING: 'polling', // Textract async — waiting on provider
    SUCCEEDED: 'succeeded',
    FAILED: 'failed',
};
// -------------------------------------------------------------------------
// Review queue — any item flagged for human eyes before UC1/UC2/UC3 run.
// -------------------------------------------------------------------------
export const REVIEW_ITEM_TYPE = {
    CLASSIFICATION: 'classification',
    EXTRACTION: 'extraction',
};
export const REVIEW_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved', // human confirmed AI output
    CORRECTED: 'corrected', // human changed the AI output
    REJECTED: 'rejected', // human rejected outright (doc unusable)
    NOT_REQUIRED: 'not_required', // confidence above threshold; auto-approved
};
// -------------------------------------------------------------------------
// Bedrock model identifiers. All values are AWS Bedrock "inference profile
// IDs" in ap-south-1 (Mumbai). Switching the active model is a masters/config
// change, NOT a code change — these values are the *defaults* that masters
// seeds with.
// -------------------------------------------------------------------------
export const BEDROCK_MODEL = {
    /** Claude 3.5 Sonnet via Bedrock in ap-south-1 */
    CLAUDE_35_SONNET: 'apac.anthropic.claude-3-5-sonnet-20241022-v2:0',
    /** Claude 3.5 Haiku — cheaper, used for high-volume classification */
    CLAUDE_35_HAIKU: 'apac.anthropic.claude-3-5-haiku-20241022-v1:0',
    /** Claude Opus 4 — used for high-stakes extraction */
    CLAUDE_OPUS_4: 'apac.anthropic.claude-opus-4-20250514-v1:0',
};
// -------------------------------------------------------------------------
// Masters type keys for parsing. Seeded by backend/prisma/seed.ts.
// -------------------------------------------------------------------------
export const PARSING_MASTERS_TYPE = {
    /** LLM prompts — classification + extraction + review */
    LLM_PROMPTS: 'llm_prompts',
    /** Per-doc-type extraction JSON schemas */
    EXTRACTION_SCHEMAS: 'extraction_schemas',
};
// -------------------------------------------------------------------------
// Prompt category — for discovery. A record of type `llm_prompts` has a
// `category` field which is one of these values. The extraction category
// is further qualified by `doc_type` inside the record.
// -------------------------------------------------------------------------
export const PROMPT_CATEGORY = {
    CLASSIFICATION: 'classification',
    EXTRACTION: 'extraction',
    REVIEW_SUMMARY: 'review_summary',
};
// -------------------------------------------------------------------------
// Admin config keys owned by the parsing module. Seeded by parsing-config
// seeder; editable via the admin config editor.
// -------------------------------------------------------------------------
export const PARSING_CONFIG_KEY = {
    CLASSIFICATION_MIN_CONFIDENCE: 'parsing.classification.min_confidence', // default 0.75
    EXTRACTION_MIN_CONFIDENCE: 'parsing.extraction.min_confidence', // default 0.70
    EXTRACTION_FIELD_MIN_CONFIDENCE: 'parsing.extraction.field_min_confidence', // default 0.65
    BEDROCK_MODEL_CLASSIFICATION: 'parsing.bedrock.model.classification', // default BEDROCK_MODEL.CLAUDE_35_HAIKU
    BEDROCK_MODEL_EXTRACTION: 'parsing.bedrock.model.extraction', // default BEDROCK_MODEL.CLAUDE_35_SONNET
    BEDROCK_REGION: 'parsing.bedrock.region', // default 'ap-south-1'
    BEDROCK_MAX_RETRIES: 'parsing.bedrock.max_retries', // default 3
    BEDROCK_RATE_LIMIT_TPM: 'parsing.bedrock.rate_limit_tpm', // default 200_000
    // Sprint 7: Claude Vision OCR config
    OCR_PAGES_PER_REQUEST: 'parsing.ocr.pages_per_request', // default 5
    OCR_VIEWPORT_SCALE: 'parsing.ocr.viewport_scale', // default 2.0
};
// -------------------------------------------------------------------------
// Sprint 5 classification taxonomy — the 10 core doc types + other + unclassified.
// Kept as a subset reference; the full DOCUMENT_TYPE enum remains the
// authoritative list. New types bind in later sprints without code change
// because schemas/prompts live in masters.
// -------------------------------------------------------------------------
export const PARSING_CLASSIFIABLE_TYPES = [
    'sanction_letter',
    'kfs',
    'loan_agreement',
    'cam',
    'kyc',
    'itr',
    'form_26as',
    'bank_statement',
    'salary_slip',
    'valuation',
    'legal_technical_report',
    'cibil_report',
    'cfa_excel',
    'application_form',
    'other',
    'unclassified',
];
// -------------------------------------------------------------------------
// S3 prefixes — all parsing artefacts live under S3_BUCKET_FILES with these
// prefixes. One prefix per artefact kind so object-lifecycle policies can
// target them individually.
// -------------------------------------------------------------------------
export const PARSING_S3_PREFIX = {
    OCR_TEXT: 'ocr/text', // /{docId}.txt
    OCR_PAGES: 'ocr/pages', // /{docId}.json
    OCR_RAW_TEXTRACT: 'ocr/raw', // /{docId}/raw.json — kept-name-for-compat; now Claude Vision raw response
    CLASSIFICATION: 'parsing/classification', // /{docId}.json
    EXTRACTION: 'parsing/extraction', // /{docId}.json
};
//# sourceMappingURL=parsing.js.map